/**
 * Iter-69: Pure helpers for sortable + filterable + searchable tables.
 *
 * Designed to be composed inside `useTableControls` hook so any table can
 * opt in with a single config array. No React imports here — kept pure
 * to keep the hook small and testable.
 */

/**
 * Sort-aware comparison. Handles numbers, strings (case-insensitive),
 * dates, booleans, null/undefined (always last regardless of direction).
 */
export function compareValues(a, b) {
  // Both nullish → equal
  const aNil = a === null || a === undefined || a === "";
  const bNil = b === null || b === undefined || b === "";
  if (aNil && bNil) return 0;
  if (aNil) return 1; // nulls sink
  if (bNil) return -1;

  // Numbers
  if (typeof a === "number" && typeof b === "number") return a - b;

  // Dates (Date instances OR ISO strings parseable)
  if (a instanceof Date || b instanceof Date) {
    const ad = a instanceof Date ? a.getTime() : new Date(a).getTime();
    const bd = b instanceof Date ? b.getTime() : new Date(b).getTime();
    return ad - bd;
  }

  // Booleans
  if (typeof a === "boolean" && typeof b === "boolean") return a === b ? 0 : a ? -1 : 1;

  // Fallback: case-insensitive string
  return String(a).toLowerCase().localeCompare(String(b).toLowerCase());
}

/**
 * Run an accessor over a row to produce the comparable value. The accessor
 * is either a string key ("name") or a function (row) => value.
 */
export function getAccessorValue(row, accessor) {
  if (typeof accessor === "function") return accessor(row);
  if (typeof accessor === "string") return row?.[accessor];
  return undefined;
}

/**
 * Sort an array of rows by the given column config + direction.
 * direction === "none" returns input unchanged (stable).
 */
export function sortRows(rows, columnConfig, direction) {
  if (!direction || direction === "none" || !columnConfig) return rows;
  const sorted = [...rows];
  sorted.sort((ra, rb) => {
    const a = getAccessorValue(ra, columnConfig.accessor);
    const b = getAccessorValue(rb, columnConfig.accessor);
    const cmp = compareValues(a, b);
    return direction === "asc" ? cmp : -cmp;
  });
  return sorted;
}

/**
 * Compute distinct stringified values for a column — used to populate
 * filter dropdowns. Falsy values render as "—" (matched on save).
 */
export function getDistinctValues(rows, accessor, formatter) {
  const set = new Set();
  for (const row of rows) {
    const raw = getAccessorValue(row, accessor);
    const v = formatter ? formatter(raw, row) : raw;
    if (v === null || v === undefined || v === "") {
      set.add("(none)");
    } else {
      set.add(String(v));
    }
  }
  // Stable alpha sort
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

/**
 * Apply per-column filters. `filters` is an object keyed by columnKey,
 * each value is a Set<string> of allowed display values (or null = no filter).
 */
export function applyColumnFilters(rows, columns, filters) {
  if (!filters || Object.keys(filters).length === 0) return rows;
  return rows.filter((row) => {
    for (const col of columns) {
      const selected = filters[col.key];
      if (!selected || selected.size === 0) continue;
      const raw = getAccessorValue(row, col.accessor);
      const v = col.filterFormatter ? col.filterFormatter(raw, row) : raw;
      const display = v === null || v === undefined || v === "" ? "(none)" : String(v);
      if (!selected.has(display)) return false;
    }
    return true;
  });
}

/**
 * Free-text search across all columns marked `searchable: true` (or all
 * columns if none flagged). Match is substring, case-insensitive.
 */
export function applyGlobalSearch(rows, columns, query) {
  const q = (query || "").trim().toLowerCase();
  if (!q) return rows;
  const searchCols = columns.filter((c) => c.searchable !== false);
  return rows.filter((row) => {
    for (const col of searchCols) {
      const raw = getAccessorValue(row, col.accessor);
      if (raw === null || raw === undefined) continue;
      const haystack = (col.searchFormatter ? col.searchFormatter(raw, row) : String(raw)).toLowerCase();
      if (haystack.includes(q)) return true;
    }
    return false;
  });
}
